import { useEffect, useState } from 'react';

import { format } from 'date-fns';
import { useNavigate } from 'react-router';
import { useDebouncedCallback } from 'use-debounce';
import { NumberParam, StringParam, useQueryParams } from 'use-query-params';

import {
  Typography,
  TableContainer,
  Table,
  TableRow,
  TableCell,
  TableHead,
  TableBody,
  TableSortLabel,
  Box,
  styled,
  Button,
} from '@mui/material';
import { styled as muiStyled } from '@mui/material';
import { visuallyHidden } from '@mui/utils';

import { CreateButton } from '../../../components/buttons/CreateButton';
import StatusChip from '../../../components/chips/StatusChip';
import { ConfirmDialog } from '../../../components/dialogs';
import { SearchField } from '../../../components/inputs';
import { Pagination } from '../../../components/paginations';
import { Spinner } from '../../../components/spinners';
import { Defaults } from '../../../constants';
import { useDenyReportedUser } from '../../../lib/report/hooks';
import useGetAllReportedUsers from '../../../lib/report/hooks/useGetAllReportedUsers';
import { SortDirection } from '../../../lib/util/helpers';

const ReportedUsersList = () => {
  const navigate = useNavigate();
  const [optionsUpdated, setOptionsUpdated] = useState(true);
  const handleAllUsers = () => navigate(`/cms/users`);
  const handleEditUser = (id: number) => navigate(`/cms/users/reported/${id}`);
  const denyReportedUser = useDenyReportedUser();
  const [query, setQuery] = useQueryParams({
    page: NumberParam,
    search: StringParam,
    sort: StringParam,
  });
  const { page, search, sort } = query;
  const searchString = search && { search };
  const debouncedSearchFn = useDebouncedCallback((value: string) => {
    setQuery({ search: value, page: 0 });
  }, Defaults.SEARCH_DEBOUNCE_MS);

  const {
    data: reportedUser,
    isLoading,
    error,
  } = useGetAllReportedUsers({
    page: Number(page) || 0,
    sort: String(sort || ''),
    ...searchString,
  });

  useEffect(() => {
    if (!optionsUpdated) {
      return;
    }
    setOptionsUpdated(false);
  }, [optionsUpdated, page, sort, search]);

  useEffect(() => {
    setOptionsUpdated(true);
  }, [setOptionsUpdated, page, sort, search]);

  const setNewSort = (column: string, direction: SortDirection) => {
    const newSort = direction ? `${column} ${direction}` : undefined;
    setQuery({ sort: newSort });
  };

  const setNewPage = (page: number) => setQuery({ page: page - 1 });

  const sortToObj = (sortStr: string | undefined) => {
    const initialValues = { column: '', direction: undefined };
    if (typeof sortStr !== 'string') {
      return initialValues;
    }
    const [column, direction] = sortStr.split(' ');
    if (typeof column !== 'string' || !['asc', 'desc', undefined].includes(direction)) {
      return initialValues;
    }
    return { ...initialValues, column, direction: direction as SortDirection };
  };

  const sortObj = sortToObj(sort ?? '');

  const columns = [
    {
      text: 'Date',
      name: 'user.lastReportedDate',
    },
    {
      text: 'User ID',
      name: 'user.id',
    },
    {
      text: 'Username',
      name: 'user.username',
    },
    {
      text: 'First Name',
      name: 'user.firstName',
    },
    {
      text: 'Last Name',
      name: 'user.lastName',
    },
    {
      text: 'Reported Count',
      name: 'user_report_count',
    },
    {
      text: 'Status',
      name: 'user.deactivated',
    },
    {
      text: 'Actions',
      name: 'actions',
    },
  ];

  return (
    <Container>
      <FullWidthBar>
        <Typography variant="h5" sx={{ marginRight: '20px' }}>
          Reported Users
        </Typography>
        <CreateButton variant="contained" onClick={handleAllUsers}>
          Back to Users List
        </CreateButton>
        <StyledSearchField
          searchString={search ?? ''}
          onSearch={(search: string) => {
            debouncedSearchFn(search);
          }}
        />
      </FullWidthBar>

      <TableContainer sx={{ backgroundColor: 'white' }}>
        <Table aria-labelledby="tableTitle">
          <TableHead>
            <PointerTableRow>
              {columns.map((column) => (
                <TableCell key={column.name}>
                  <TableSortLabel
                    active={sortObj.column === column.name}
                    direction={sortObj.direction}
                    onClick={() => {
                      const isAsc = sortObj.column === column.name && sortObj.direction === 'asc';
                      setNewSort(column.name, isAsc ? 'desc' : 'asc');
                    }}
                    component="a"
                  >
                    {column.text}
                    {sortObj.column === column.name ? (
                      <Box component="span" sx={visuallyHidden}>
                        {sortObj.direction === 'desc' ? 'sorted descending' : 'sorted ascending'}
                      </Box>
                    ) : null}
                  </TableSortLabel>
                </TableCell>
              ))}
            </PointerTableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={3}>
                  <Spinner />
                </TableCell>
              </TableRow>
            )}
            {!isLoading &&
              !error &&
              reportedUser.data &&
              reportedUser.data.map((row: any, index: number) => {
                return (
                  <StyledTableRow key={index}>
                    <TableCell>{format(new Date(row.lastReportedDate), 'MM/dd/yyyy hh:mm aaa')}</TableCell>
                    <TableCell>{row.id}</TableCell>
                    <TableCell>{row.username}</TableCell>
                    <TableCell>{row.firstName}</TableCell>
                    <TableCell>{row.lastName}</TableCell>
                    <TableCell>{row.reportCount}</TableCell>
                    <TableCell>
                      <StatusChip deactivated={Boolean(row.deactivated)} reportDenied={isReportDenied(row)} />
                    </TableCell>
                    <TableCell>
                      <Button onClick={() => handleEditUser(row.id)} type="button" style={{ textTransform: 'capitalize' }}>
                        Edit
                      </Button>
                      <ConfirmDialog
                        trigger={(open) => (
                          <Button
                            onClick={open}
                            type="button"
                            style={{ textTransform: 'capitalize' }}
                            disabled={Boolean(row.deactivated || isReportDenied(row) || denyReportedUser.isLoading)}
                          >
                            Deny Report
                          </Button>
                        )}
                        title="Deny Report"
                        description="Are you sure you want to deny this report? The user will remain active."
                        cancelBtnText="Cancel"
                        submitBtnText="Deny Report"
                        loading={denyReportedUser.isLoading}
                        onConfirm={async (close) => {
                          await denyReportedUser.mutateAsync(row.id);
                          close();
                        }}
                      />
                    </TableCell>
                  </StyledTableRow>
                );
              })}
            {reportedUser.data.length === 0 && (
              <StyledTableRow>
                <TableCell colSpan={5} style={{ textAlign: 'center' }}>
                  No reported users records found.
                </TableCell>
              </StyledTableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {reportedUser.total !== null && reportedUser.total !== 0 && (
        <FullWidthBar>
          <Typography variant="subtitle1">Total: {reportedUser.total}</Typography>
          <StyledPagination
            current={Number(page) + 1 || 1}
            total={Math.ceil(reportedUser.total / Defaults.LIST_PAGE_SIZE)}
            onPageChange={(page: number) => setNewPage(page)}
          />
        </FullWidthBar>
      )}
    </Container>
  );
};

const isReportDenied = (reportedUser: any) =>
  Boolean(reportedUser.reportDenied || reportedUser.denied || reportedUser.reportStatus === 'Report Denied' || reportedUser.status === 'Report Denied');

const Container = styled('div')({
  padding: '20px',
  flex: 1,
  display: 'flex',
  alignItems: 'stretch',
  flexDirection: 'column',
});

const FullWidthBar = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  margin: '8px 0',
  justifyContent: 'space-between',
  alignItems: 'center',
});

const PointerTableRow = styled(TableRow)({
  cursor: 'pointer',
});

const StyledTableRow = muiStyled(PointerTableRow)(({ theme }) => ({
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const StyledPagination = styled(Pagination)({
  alignSelf: 'flex-end',
});

const StyledSearchField = styled(SearchField)({
  marginLeft: 'auto',
});

export default ReportedUsersList;
